import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Buildings, Users, Lock, CheckCircle, ArrowRight, ScrollIcon, Receipt,
} from '@phosphor-icons/react';

/** Public Developer-tier landing / upgrade page.
 *
 * Routes here:
 *   - From Landing footer
 *   - From Account → "Upgrade to Developer" CTA (Foundation tier only)
 *   - From the Upgrade modal lifetime panel for users who want the multi-seat path
 *
 * Anyone can VIEW the page (no auth). Clicking "Subscribe" prompts a
 * sign-in if they're anonymous; otherwise it kicks off the Stripe
 * subscription Checkout via /api/b2b/checkout-session.
 */
export default function DeveloperUpgrade() {
    const { user } = useAuth();
    const [teamName, setTeamName] = useState('');
    const [loading, setLoading] = useState(false);

    const subscribe = async () => {
        if (!user) {
            toast('Sign in with Google first, then come back to subscribe.');
            window.location.href = '/';
            return;
        }
        if (!teamName.trim()) {
            toast.error('Give your team a name first.');
            return;
        }
        setLoading(true);
        try {
            const res = await axios.post(
                `${API}/b2b/checkout-session`,
                { team_name: teamName.trim(), origin_url: window.location.origin },
                { withCredentials: true },
            );
            if (res.data.already_subscribed) {
                toast.success('You already have an active Developer subscription.');
                window.location.href = '/team';
                return;
            }
            if (!res.data?.url) {
                toast.error('Could not start checkout. Try again.');
                setLoading(false);
                return;
            }
            window.location.href = res.data.url;
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not start checkout. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Checkout failed');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white text-[#09090B]">
            <header className="border-b border-[#18181B] sticky top-0 bg-white z-30">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                    <Link to="/" className="inline-flex items-center gap-2" data-testid="developer-page-home">
                        <img src="/defectr-logo.png" alt="DefectR" className="h-8 w-auto object-contain" />
                    </Link>
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">
                        For housebuilders
                    </span>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="developer-upgrade-page">
                {/* Hero */}
                <section className="mb-12">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1787AB] mb-3">
                        DefectR Developer
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                        <span className="block">Run your snagging</span>
                        <span className="inline-block bg-[#1FA5CF] text-white px-3 py-0.5 mt-2">
                            at site scale.
                        </span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg text-[#52525B] max-w-2xl leading-relaxed">
                        Unlimited properties, unlimited defects, your own branding on every PDF.
                        Multi-seat licences for your site managers, project leads and aftercare team —
                        with a daily digest so nobody misses a defect logged by a homeowner.
                    </p>
                </section>

                {/* Pricing card */}
                <section className="grid md:grid-cols-2 gap-6 mb-12">
                    <div className="border-2 border-[#1FA5CF] bg-[#E8F5FB] p-6" data-testid="developer-tier-card">
                        <div className="flex items-baseline justify-between">
                            <div className="font-display text-2xl font-bold">Developer</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1787AB]">Annual</div>
                        </div>
                        <div className="mt-4 font-display text-5xl font-bold text-[#09090B]">£4,800<span className="text-base font-mono text-[#52525B] uppercase tracking-wider ml-2">/ year</span></div>
                        <div className="mt-1 text-xs font-mono uppercase tracking-[0.18em] text-[#52525B]">25 seats · £192 per seat per year</div>

                        <ul className="mt-6 space-y-2 text-sm">
                            <Feature>Unlimited properties &amp; defects (every seat)</Feature>
                            <Feature>Multi-seat invites with one-click revoke</Feature>
                            <Feature>Branded PDF reports (your logo &amp; colour)</Feature>
                            <Feature>CSV bulk-import: plots → DefectR in seconds</Feature>
                            <Feature>Daily digest of new defects per builder</Feature>
                            <Feature>Stripe-managed billing, cancel any time</Feature>
                        </ul>

                        {/* Form */}
                        <div className="mt-6">
                            <label htmlFor="team-name" className="label-tiny block mb-1.5">Your team name</label>
                            <input
                                id="team-name"
                                data-testid="developer-team-name-input"
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value.slice(0, 120))}
                                placeholder="e.g. Bigwood Homes Aftercare"
                                className="w-full border border-[#18181B] focus:outline-none focus:ring-2 focus:ring-[#1FA5CF] px-3 py-3"
                            />
                            <button
                                onClick={subscribe}
                                disabled={loading || !teamName.trim()}
                                data-testid="developer-subscribe-btn"
                                className={`mt-3 w-full py-3.5 font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 ${
                                    loading || !teamName.trim()
                                        ? 'bg-[#A1A1AA] text-white cursor-not-allowed'
                                        : 'bg-[#1FA5CF] text-white hover:bg-[#1787AB] active:scale-95 transition-all duration-150'
                                }`}
                            >
                                {loading ? 'Opening Stripe…' : (<>Subscribe — £4,800 / year <ArrowRight size={16} weight="bold" /></>)}
                            </button>
                            <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#52525B] text-center">
                                Payment by Stripe · Cancel anytime · UK VAT inclusive
                            </p>
                            <p className="mt-2 text-[10px] text-[#52525B] text-center">
                                By subscribing you agree to our{' '}
                                <Link to="/terms" className="underline hover:text-[#1787AB]">Terms &amp; refunds</Link>.
                            </p>
                        </div>
                    </div>

                    <div className="border-2 border-[#18181B] p-6 flex flex-col" data-testid="enterprise-tier-card">
                        <div className="flex items-baseline justify-between">
                            <div className="font-display text-2xl font-bold">Enterprise</div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">Custom</div>
                        </div>
                        <div className="mt-4 font-display text-3xl font-bold text-[#09090B]">Talk to us</div>
                        <div className="mt-1 text-xs font-mono uppercase tracking-[0.18em] text-[#52525B]">100+ seats · bespoke contract</div>

                        <ul className="mt-6 space-y-2 text-sm flex-1">
                            <Feature>Everything in Developer</Feature>
                            <Feature>Custom seat count (100, 500, 1,000+)</Feature>
                            <Feature>Procurement-friendly annual invoice (PO + bank transfer)</Feature>
                            <Feature>Priority support &amp; named contact</Feature>
                            <Feature>Onboarding session for your team</Feature>
                        </ul>

                        <a
                            href="mailto:support@defectr.co.uk?subject=DefectR%20Enterprise%20quote%20request"
                            data-testid="enterprise-contact-link"
                            className="mt-6 inline-flex items-center justify-center gap-2 py-3.5 border-2 border-[#18181B] hover:bg-[#18181B] hover:text-white font-mono text-xs uppercase tracking-[0.2em] active:scale-95 transition-all duration-150"
                        >
                            Email us for a quote <ArrowRight size={16} weight="bold" />
                        </a>
                    </div>
                </section>

                {/* Why */}
                <section className="grid md:grid-cols-3 gap-6 mb-12">
                    <WhyCard
                        icon={<Buildings size={22} weight="duotone" color="#1FA5CF" />}
                        title="Built around your sites"
                        body="Bulk-import every plot from a CSV, assign managers per development, and the whole team works off the same defect list."
                    />
                    <WhyCard
                        icon={<Users size={22} weight="duotone" color="#1FA5CF" />}
                        title="25 seats, one billing line"
                        body="Add aftercare staff, site managers, snagging surveyors — revoke any of them in one click. Stripe billing is annual + clean."
                    />
                    <WhyCard
                        icon={<ScrollIcon size={22} weight="duotone" color="#1FA5CF" />}
                        title="Your brand, not ours"
                        body="Upload your logo and brand colour and every PDF report goes out under YOUR identity — perfect for handover packs."
                    />
                </section>

                {/* Compliance footer */}
                <section className="border-t border-[#E4E4E7] pt-6">
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#52525B] flex flex-wrap items-center gap-4">
                        <Receipt size={14} weight="bold" />
                        <span>VAT-inclusive UK pricing</span>
                        <span>·</span>
                        <span>Annual subscription</span>
                        <span>·</span>
                        <span>Cancellable in your Stripe portal</span>
                        <span>·</span>
                        <Link to="/terms" className="underline hover:text-[#1787AB]">Refund policy</Link>
                    </div>
                </section>
            </main>
        </div>
    );
}

function Feature({ children }) {
    return (
        <li className="flex items-start gap-2">
            <CheckCircle size={14} weight="fill" color="#00875A" className="mt-0.5 flex-none" />
            <span>{children}</span>
        </li>
    );
}

function WhyCard({ icon, title, body }) {
    return (
        <div className="border border-[#18181B] p-5">
            <div>{icon}</div>
            <h3 className="font-display font-semibold mt-2">{title}</h3>
            <p className="mt-1 text-sm text-[#52525B]">{body}</p>
        </div>
    );
}
